/**
 * DriversSection — Chauffeursbeheer onder Instellingen → Chauffeurs.
 *
 * Verantwoordelijkheden:
 *   - lijst chauffeurs (zoek + actief/inactief filter)
 *   - nieuwe chauffeur toevoegen
 *   - chauffeur bewerken
 *   - chauffeur deactiveren (soft delete)
 *
 * Privacy: telefoon/whatsapp/e-mail zijn alleen zichtbaar voor RBAC'd users
 * (gating gebeurt op de backend). Frontend laat geen public exposure toe —
 * deze sectie hangt onder InstellingenSection achter de admin-login.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Switch } from "../../ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "../../ui/dialog";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Search, RefreshCw, Truck, Phone, Mail, MessageCircle,
} from "lucide-react";

const CHANNELS = [
  { value: "whatsapp_link", label: "WhatsApp-link" },
  { value: "email", label: "E-mail" },
];
const NAVIGATIONS = [
  { value: "google_maps", label: "Google Maps" },
  { value: "waze", label: "Waze" },
  { value: "apple_maps", label: "Apple Maps" },
];
const COUNTRIES = [
  { value: "NL", label: "Nederland (+31)" },
  { value: "CW", label: "Curaçao (+599)" },
  { value: "BE", label: "België (+32)" },
  { value: "DE", label: "Duitsland (+49)" },
  { value: "FR", label: "Frankrijk (+33)" },
];

const EMPTY_FORM = {
  name: "", phone: "", whatsapp: "", email: "", active: true,
  vehicle_plate: "", default_region: "",
  preferred_channel: "whatsapp_link",
  preferred_navigation: "google_maps",
  internal_note: "", default_country: "NL",
};

export default function DriversSection() {
  const { user } = useAuth();
  const canManage = ["owner", "admin", "manager"].includes(user?.role);

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all | active | inactive
  const [editing, setEditing] = useState(null); // null | driver-object | "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (activeFilter === "active") params.active = true;
      if (activeFilter === "inactive") params.active = false;
      const r = await adminApi.get("/admin/drivers", { params });
      setDrivers(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [search, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };
  const openEdit = (d) => {
    setForm({
      name: d.name || "",
      phone: d.phone || "",
      whatsapp: d.whatsapp || "",
      email: d.email || "",
      active: d.active !== false,
      vehicle_plate: d.vehicle_plate || "",
      default_region: d.default_region || "",
      preferred_channel: d.preferred_channel || "whatsapp_link",
      preferred_navigation: d.preferred_navigation || "google_maps",
      internal_note: d.internal_note || "",
      default_country: d.default_country || "NL",
    });
    setEditing(d);
  };
  const closeDialog = () => { setEditing(null); setForm(EMPTY_FORM); };

  const onSave = async () => {
    if (!form.name.trim()) { toast.error("Naam is verplicht."); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await adminApi.post("/admin/drivers", form);
        toast.success("Chauffeur toegevoegd.");
      } else {
        await adminApi.patch(`/admin/drivers/${editing.id}`, form);
        toast.success("Chauffeur bijgewerkt.");
      }
      closeDialog();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onDelete = async (d) => {
    if (!window.confirm(`Chauffeur "${d.name}" deactiveren? Dispatchgeschiedenis blijft bewaard.`)) return;
    try {
      await adminApi.delete(`/admin/drivers/${d.id}`);
      toast.success("Chauffeur gedeactiveerd.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-gray-200 p-4 sm:p-6" data-testid="drivers-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-chivo font-bold text-lg text-[#0A192F]">Chauffeurs</h2>
          <p className="text-sm text-gray-600 mt-1">
            Beheer chauffeurs voor pickup &amp; delivery dispatch via WhatsApp-link of e-mail.
            Telefoonnummers worden alleen in de backoffice getoond.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" className="rounded-none" data-testid="drivers-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Vernieuwen
          </Button>
          {canManage && (
            <Dialog open={editing === "new"} onOpenChange={(o) => { if (!o) closeDialog(); }}>
              <DialogTrigger asChild>
                <Button onClick={openNew} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="drivers-new-button">
                  <Plus className="w-4 h-4 mr-1" /> Nieuwe chauffeur
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
                {renderForm("new")}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-0 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="drivers-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek op naam, kenteken of e-mail"
            className="rounded-none pl-9 w-full min-w-0"
          />
        </div>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="rounded-none w-40" data-testid="drivers-active-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle chauffeurs</SelectItem>
            <SelectItem value="active">Alleen actief</SelectItem>
            <SelectItem value="inactive">Alleen inactief</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-gray-500 text-sm">Laden…</div>
        ) : drivers.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Geen chauffeurs.</div>
        ) : (
          <table className="w-full text-sm" data-testid="drivers-table">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide font-chivo font-bold text-gray-700">
              <tr>
                <th className="p-3">Naam</th>
                <th className="p-3 hidden sm:table-cell">Contact</th>
                <th className="p-3 hidden md:table-cell">Voertuig</th>
                <th className="p-3 hidden md:table-cell">Voorkeur</th>
                <th className="p-3 text-center">Actief</th>
                {canManage && <th className="p-3 text-right">Acties</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {drivers.map((d) => (
                <tr key={d.id} className={!d.active ? "opacity-60" : ""} data-testid={`driver-row-${d.id}`}>
                  <td className="p-3">
                    <div className="font-chivo font-bold text-[#0A192F] flex items-center gap-2">
                      <Truck className="w-4 h-4 text-[#0055FF]" /> {d.name}
                    </div>
                    {d.default_region && <div className="text-xs text-gray-500">Regio: {d.default_region}</div>}
                  </td>
                  <td className="p-3 hidden sm:table-cell">
                    <div className="text-xs text-gray-600 space-y-0.5">
                      {d.phone_e164 && (<div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {d.phone_e164}</div>)}
                      {d.whatsapp_e164 && (<div className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {d.whatsapp_e164}</div>)}
                      {d.email && (<div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {d.email}</div>)}
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell text-xs text-gray-600">{d.vehicle_plate || "—"}</td>
                  <td className="p-3 hidden md:table-cell text-xs text-gray-600">
                    {CHANNELS.find((c) => c.value === d.preferred_channel)?.label || "—"}
                    <div className="text-[10px] text-gray-400">{NAVIGATIONS.find((n) => n.value === d.preferred_navigation)?.label || ""}</div>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${d.active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>
                      {d.active ? "Ja" : "Nee"}
                    </span>
                  </td>
                  {canManage && (
                    <td className="p-3 text-right">
                      <button onClick={() => openEdit(d)} className="p-1.5 text-gray-400 hover:text-[#0055FF]" title="Bewerken" data-testid={`driver-edit-${d.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => onDelete(d)} className="p-1.5 text-gray-400 hover:text-red-500" title="Deactiveren" data-testid={`driver-delete-${d.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit dialog (gedeeld met New via DialogTrigger; renderForm is hieronder) */}
      <Dialog open={editing !== null && editing !== "new"} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
          {renderForm("edit")}
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderForm(mode) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{mode === "new" ? "Nieuwe chauffeur" : "Chauffeur bewerken"}</DialogTitle>
          <DialogDescription>
            Telefoon, WhatsApp en e-mail zijn alleen zichtbaar in de backoffice. Verplicht: naam.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Naam *</Label>
            <Input data-testid="driver-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1 w-full min-w-0" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">Telefoon</Label>
              <Input data-testid="driver-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0612345678" className="rounded-none mt-1 w-full min-w-0" />
            </div>
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">WhatsApp</Label>
              <Input data-testid="driver-form-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="Idem als telefoon mag" className="rounded-none mt-1 w-full min-w-0" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">E-mail</Label>
              <Input type="email" data-testid="driver-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1 w-full min-w-0" />
            </div>
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">Standaard landcode</Label>
              <Select value={form.default_country} onValueChange={(v) => setForm({ ...form, default_country: v })}>
                <SelectTrigger className="rounded-none mt-1 w-full" data-testid="driver-form-country"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">Voorkeurkanaal</Label>
              <Select value={form.preferred_channel} onValueChange={(v) => setForm({ ...form, preferred_channel: v })}>
                <SelectTrigger className="rounded-none mt-1 w-full" data-testid="driver-form-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">Voorkeur navigatie</Label>
              <Select value={form.preferred_navigation} onValueChange={(v) => setForm({ ...form, preferred_navigation: v })}>
                <SelectTrigger className="rounded-none mt-1 w-full" data-testid="driver-form-navigation"><SelectValue /></SelectTrigger>
                <SelectContent>{NAVIGATIONS.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">Voertuig / kenteken</Label>
              <Input data-testid="driver-form-vehicle" value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value })} className="rounded-none mt-1 w-full min-w-0" />
            </div>
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">Standaard regio</Label>
              <Input data-testid="driver-form-region" value={form.default_region} onChange={(e) => setForm({ ...form, default_region: e.target.value })} placeholder="bijv. Curaçao / Randstad" className="rounded-none mt-1 w-full min-w-0" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold">Interne notitie</Label>
            <Input data-testid="driver-form-note" value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })} className="rounded-none mt-1 w-full min-w-0" />
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <Label className="text-xs uppercase tracking-wide font-bold flex items-center gap-2">
              Actief
            </Label>
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="driver-form-active" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closeDialog} className="rounded-none">Annuleren</Button>
          <Button onClick={onSave} disabled={saving} className="rounded-none bg-[#050505] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" data-testid="driver-form-save">
            {saving ? "Opslaan…" : (mode === "new" ? "Toevoegen" : "Opslaan")}
          </Button>
        </DialogFooter>
      </>
    );
  }
}
